/**
 * ViewGrid Error Boundary
 *
 * Error boundary component to catch and display errors gracefully.
 */

import React, { Component, ErrorInfo, ReactNode } from "react";
import { Box, Alert, Button, Typography } from "@mui/joy";
import RefreshIcon from "@mui/icons-material/Refresh";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class ViewGridErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ViewGrid error:", error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    this.props.onReset?.();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Alert
            color="danger"
            variant="soft"
            sx={{ mb: 2, justifyContent: "center" }}
          >
            Something went wrong loading the data grid.
          </Alert>
          <Typography level="body-sm" sx={{ mb: 2, color: "text.secondary" }}>
            {this.state.error?.message || "An unexpected error occurred."}
          </Typography>
          <Button
            variant="outlined"
            color="neutral"
            startDecorator={<RefreshIcon />}
            onClick={this.handleReset}
          >
            Try Again
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}
